import baseRobots from "./humanoid-robots.json";
import catalogAdditions from "./humanoid-robots-additions.json";
import { humanoidRobotAdditions as additionalRobots } from "./humanoid-robots-additions.mjs";
import baseManufacturerRecords from "./humanoid-manufacturers.json";
import { humanoidManufacturerAdditions as additionalManufacturerRecords } from "./humanoid-manufacturers-additions.mjs";
import imageSources from "./humanoid-image-sources.json";

const robots = [...baseRobots, ...additionalRobots, ...catalogAdditions];
const manufacturerRecords = [...baseManufacturerRecords, ...additionalManufacturerRecords];

export const humanoidRobots = robots;
export const humanoidImageSources = imageSources;

export const countryFlagByCode = {
  CN: "🇨🇳",
  NO: "🇳🇴",
  FR: "🇫🇷",
  ES: "🇪🇸",
  GB: "🇬🇧",
  US: "🇺🇸",
  JP: "🇯🇵",
  KR: "🇰🇷",
  IT: "🇮🇹",
  IR: "🇮🇷",
  CA: "🇨🇦",
  DE: "🇩🇪",
};

export function slugifyCountry(value = "") {
  return String(value)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

export function getPurchasePriceEntries(robot) {
  return (robot?.prices || []).filter((entry) =>
    Number.isFinite(entry.amount) && entry.currency &&
    !entry.billingPeriod && !["deposit", "subscription"].includes(entry.priceKind)
  );
}

export function getNumericPrice(robot, currency = "USD") {
  const values = getPurchasePriceEntries(robot)
    .filter((entry) => entry.currency === currency)
    .map((entry) => entry.amount);
  return values.length ? Math.min(...values) : null;
}

export function getPrimaryPrice(robot) {
  if (robot?.prices?.length) return robot.prices[0].displayPrice;
  return robot?.priceStatus || "Price not published";
}

export function getSpecificationLabel(robot, field) {
  const labels = { heightCm: "heightLabel", weightKg: "weightLabel", degreesOfFreedom: "degreesOfFreedomLabel" };
  const units = { heightCm: " cm", weightKg: " kg", degreesOfFreedom: "" };
  const note = robot?.[labels[field]];
  if (note) return note;
  const value = robot?.[field];
  return Number.isFinite(value) ? `${value}${units[field] || ""}` : "Not disclosed in the retained sources";
}

export function getPurchasePriceLabel(robot) {
  const entries = getPurchasePriceEntries(robot);
  return entries.length ? entries.map((entry) => entry.displayPrice || `${entry.currency} ${entry.amount.toLocaleString("en-US")}`).join(" / ") : "Purchase price not recorded";
}

export const humanoidManufacturers = manufacturerRecords
  .map((manufacturer) => {
    const manufacturerRobots = robots.filter(
      (robot) => robot.manufacturerId === manufacturer.id,
    );
    const publicPrices = manufacturerRobots
      .map(getNumericPrice)
      .filter(Number.isFinite);
    return {
      ...manufacturer,
      robotCount: manufacturerRobots.length,
      publicPriceMin: publicPrices.length ? Math.min(...publicPrices) : null,
      publicPriceMax: publicPrices.length ? Math.max(...publicPrices) : null,
      lastVerifiedAt: manufacturerRobots
        .map((robot) => robot.lastVerifiedAt)
        .sort()
        .at(-1),
    };
  })
  .sort((a, b) => a.name.localeCompare(b.name));

export const humanoidCountries = Object.values(
  robots.reduce((countries, robot) => {
    const key = robot.robotOriginCountryCode;
    const numeric = getNumericPrice(robot);
    if (!countries[key]) {
      countries[key] = {
        name: robot.robotOriginCountry,
        slug: slugifyCountry(robot.robotOriginCountry),
        code: key,
        flag: countryFlagByCode[key] || "🌐",
        manufacturerIds: new Set(),
        robotIds: [],
        publicPrices: [],
        categories: new Set(),
      };
    }
    countries[key].manufacturerIds.add(robot.manufacturerId);
    countries[key].robotIds.push(robot.id);
    countries[key].categories.add(robot.category);
    if (numeric !== null) countries[key].publicPrices.push(numeric);
    return countries;
  }, {}),
)
  .map((country) => ({
    ...country,
    manufacturerCount: country.manufacturerIds.size,
    robotCount: country.robotIds.length,
    manufacturerIds: [...country.manufacturerIds],
    categories: [...country.categories],
    minPublicPrice: country.publicPrices.length
      ? Math.min(...country.publicPrices)
      : null,
    maxPublicPrice: country.publicPrices.length
      ? Math.max(...country.publicPrices)
      : null,
  }))
  .sort((a, b) => b.robotCount - a.robotCount || a.name.localeCompare(b.name));

export const humanoidCatalogStats = {
  robotCount: robots.length,
  manufacturerCount: humanoidManufacturers.length,
  countryCount: humanoidCountries.length,
  publicPriceCount: robots.filter((robot) => robot.publicPrice).length,
  quoteCount: robots.filter((robot) => robot.priceType === "quote").length,
  nonCommercialCount: robots.filter((robot) =>
    ["not-commercialized"].includes(robot.priceType),
  ).length,
  lastVerifiedAt: robots.map((robot) => robot.lastVerifiedAt).sort().at(-1),
};

export function getRobotBySlug(slug) {
  return robots.find((robot) => robot.slug === slug);
}

export function getManufacturerById(id) {
  return humanoidManufacturers.find((manufacturer) => manufacturer.id === id);
}

export function getManufacturerBySlug(slug) {
  return humanoidManufacturers.find((manufacturer) => manufacturer.slug === slug);
}

export function getCountryBySlug(slug) {
  return humanoidCountries.find((country) => country.slug === slug);
}

export function getRobotsForManufacturer(id) {
  return robots.filter((robot) => robot.manufacturerId === id);
}

export function getRobotsForCountry(code) {
  return robots.filter((robot) => robot.robotOriginCountryCode === code);
}

export function getRelatedRobots(robot, limit = 4) {
  return robots
    .filter((candidate) => candidate.id !== robot.id)
    .sort((a, b) => {
      const aScore =
        Number(a.manufacturerId === robot.manufacturerId) * 4 +
        Number(a.category === robot.category) * 2 +
        Number(a.robotOriginCountryCode === robot.robotOriginCountryCode);
      const bScore =
        Number(b.manufacturerId === robot.manufacturerId) * 4 +
        Number(b.category === robot.category) * 2 +
        Number(b.robotOriginCountryCode === robot.robotOriginCountryCode);
      return bScore - aScore || a.name.localeCompare(b.name);
    })
    .slice(0, limit);
}

export function getRobotSeoTitle(name = "") {
  const profile = robots.find((robot) => robot.name === name);
  if (profile?.seoTitle) return profile.seoTitle;
  const candidates = [
    `${name} Specifications, Price and Sources`,
    `${name} Specifications and Sources`,
    `${name} Robot Profile and Sources`,
  ];
  return candidates.find((title) => title.length <= 65) || `${name.slice(0, 38)} Specifications`;
}

export function getManufacturerSeoTitle(name = "") {
  const candidates = [
    `${name} Humanoid Robots and Prices TechniaHQRobot`,
    `${name} Humanoid Robot Models and Prices TechniaHQRobot`,
    `${name} Humanoid Robots, Models and Prices TechniaHQRobot`,
    `${name} Robots, Models and Prices TechniaHQRobot`,
    `${name} Humanoid Robot Catalog TechniaHQRobot`,
  ];
  return candidates.find((title) => title.length >= 55 && title.length <= 65) || candidates.at(-1);
}

export function buildCatalogCollectionSchema(path, items, name, description) {
  const base = "https://www.techniahqservice.com";
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name,
    description,
    url: `${base}${path}`,
    mainEntity: {
      "@type": "ItemList",
      numberOfItems: items.length,
      itemListElement: items.map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: item.name,
        url: `${base}/humanoid-robots/${item.slug}`,
      })),
    },
  };
}




